// Roster store — owns the fetch lifecycle for the admin-only service-load
// roster panel (U9) shown in the Suggestions sub-view.
//
// Why a store (not component-local useState):
//   - Mirrors the dyno-pwa store convention: HTTP-driven panels that re-fetch
//     on a key change keep their loading/error/data triad in zustand so the
//     hosting component stays thin (see pqTuningStore for the reference).
//   - A per-window requestId guards against a slow GET resolving after the
//     operator has switched events — convention #6 in web/dyno-pwa/CLAUDE.md.
//
// The roster is a *planning* artifact materialized by the cloud_roster_evaluator
// worker; this store never mutates it — read-only, no optimistic state
// (convention #7).
//
// See docs/plans/2026-05-21-001-feat-house-feed-load-baselines-plan.md (U9).

import { create } from "zustand";
import { api, ApiError } from "@/lib/api";
import type { RosterResponse } from "@/lib/types";

export type RosterState =
  | { kind: "idle" }
  | { kind: "loading"; windowId: string }
  | { kind: "ready"; windowId: string; data: RosterResponse }
  // `forbidden` is the non-admin / 403 case — the panel hides itself.
  | { kind: "forbidden"; windowId: string }
  | { kind: "error"; windowId: string; message: string };

interface RosterStore {
  state: RosterState;
  // Incremented per fetch; a response whose id no longer matches is dropped.
  requestId: number;
  // Fetch the roster for an event. Safe to call repeatedly — each call
  // supersedes any in-flight fetch via the requestId guard.
  load: (windowId: string) => Promise<void>;
}

export const useRosterStore = create<RosterStore>((set, get) => ({
  state: { kind: "idle" },
  requestId: 0,

  load: async (windowId) => {
    if (!windowId) {
      set({ state: { kind: "idle" } });
      return;
    }
    const myId = get().requestId + 1;
    set({ requestId: myId, state: { kind: "loading", windowId } });

    try {
      const data = await api.get<RosterResponse>(
        `/api/windows/${encodeURIComponent(windowId)}/roster`,
        // The roster panel is admin-only; a 403 is an expected outcome on a
        // non-admin host, not a redirect-worthy auth failure.
        { skipAuthRedirect: true },
      );
      // Drop a stale response — the operator switched events mid-flight.
      if (get().requestId !== myId) return;
      set({ state: { kind: "ready", windowId, data } });
    } catch (e) {
      if (get().requestId !== myId) return;
      if (e instanceof Error && e.name === "AbortError") return;
      if (e instanceof ApiError && e.status === 403) {
        set({ state: { kind: "forbidden", windowId } });
        return;
      }
      set({
        state: {
          kind: "error",
          windowId,
          message: e instanceof Error ? e.message : String(e),
        },
      });
    }
  },
}));
